import React from 'react';
import useLanguage from '../../hooks/useLanguage';
import { formatDateWithDay } from '../../utils/dateFormatter';
import { money } from './pricingFormat';

/**
 * Block 2 — the next 30 days, published price only.
 *
 * There is deliberately NO suggested-price column. With the old upward-biased baseline
 * removed, what remains are thin heuristics, and printing "suggested: 260" claims 260
 * beats 240 on evidence we do not have at ~12 units. A suggestion ops learns to
 * distrust is worse than none — that is exactly how the previous page lost them
 * (tasks/pricing-guidance-phase1.md decision 20).
 *
 * Clicking a row leaves for the property calendar, which is the only place a price is
 * changed. This page never writes one.
 */
const EMPTY_ROWS = [];

const ThirtyDayTable = ({ days, onOpenDate }) => {
  const { t } = useLanguage();
  const rows = Array.isArray(days) ? days : EMPTY_ROWS;

  // Held is checked BEFORE available: a night holding a relocated guest is not sold (the
  // booking lives on another unit) and carries no stop-sell, so it fell through to
  // "متاحة" — the day grid offering for sale the very nights the scoreboard above it
  // reports as occupied.
  const state = (day) => {
    if (day.sold) return <span className="pg-tag pg-tag--sold">{t('pricing.days.sold')}</span>;
    if (day.held) return <span className="pg-tag pg-tag--held">{t('pricing.days.held')}</span>;
    if (day.stopSell) return <span className="pg-tag pg-tag--stop">{t('pricing.days.stopSell')}</span>;
    return <span className="pg-tag">{t('pricing.days.available')}</span>;
  };

  return (
    <section className="pg-block">
      <div className="pg-block__head">
        <h2 className="pg-block__title">
          <i className="bi bi-calendar3" />
          {t('pricing.days.heading')}
        </h2>
        <p className="pg-block__hint">{t('pricing.days.hint')}</p>
      </div>

      {rows.length === 0 ? (
        <p className="pg-table__empty">{t('pricing.days.empty')}</p>
      ) : (
        <div className="pg-days__scroll">
          <table className="pg-table">
            <thead>
              <tr>
                <th>{t('pricing.days.date')}</th>
                <th>
                  {t('pricing.days.published')}{' '}
                  <span className="pg-footing">{t('pricing.common.exVat')}</span>
                </th>
                <th>{t('pricing.days.state')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(day => (
                <tr
                  key={day.date}
                  className="pg-days__row"
                  tabIndex={0}
                  // No `role="button"` — overriding a <tr>'s implicit `row` role orphans its
                  // cells, so assistive tech drops the column structure and reads the whole
                  // row as one run of text. Keyboard activation is handled below instead.
                  onClick={() => onOpenDate(day.date)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onOpenDate(day.date);
                    }
                  }}
                >
                  <td>{formatDateWithDay(day.date)}</td>
                  <td data-figure>
                    {typeof day.publishedRate === 'number' && day.publishedRate > 0 ? (
                      <strong>
                        {money(day.publishedRate)} {t('pricing.common.currency')}
                      </strong>
                    ) : (
                      // Absence of a rate is UNKNOWN, never free. A date the calendar
                      // does not reach yet is a different fact from a date priced at 0,
                      // and the two must not render the same way.
                      <span className="pg-days__norate">
                        {day.hasLedgerRow
                          ? t('pricing.days.noRate')
                          : t('pricing.days.noLedgerRow')}
                      </span>
                    )}
                  </td>
                  <td>{state(day)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default ThirtyDayTable;
